import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Clock, Sparkles } from "lucide-react";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { Reveal } from "@/components/motion";

const SITE = "https://cphhighlights.com";
const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/6e5946401fd32a74_2026-10-07%2000_44_57.953%2B0200.webp";
const mapsUrl = (name, coords) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, Copenhagen`)}${
    coords ? `&query_place_id=&center=${coords[0]},${coords[1]}` : ""
  }`;

// Two kinds of market: open-air street-food yards, and roofed food halls.
const GROUPS = [
  {
    kind: "Street food",
    kindId: "street-food",
    note: "Open-air yards of food stalls and bars — pick from a dozen kitchens, grab a drink and share a long table. Best in good weather.",
    places: [
      {
        name: "Reffen",
        area: "Refshaleøen",
        rank: 1,
        coords: [55.693, 12.607],
        tags: ["Open-air", "~40 stalls", "Harbourside"],
        hours: "Seasonal: open daily from mid-March to October, roughly 12:00–21:00 (bars later). Closed over winter.",
        text: "Copenhagen's biggest street-food yard, out on the old industrial Refshaleøen waterfront — around 40 stalls of world food, bars and fire-pits with container seating and harbour views. Come hungry and graze.",
        img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/f92c345ccdf30470_2026-10-07%2000_42_57.931%2B0200.webp",
      },
      {
        name: "Broens Gadekøkken",
        area: "Christianshavn",
        coords: [55.67786, 12.59682],
        tags: ["Open-air", "Central", "Canal-side"],
        hours: "Food stalls: Sun–Thu 11:30–20:00, Fri–Sat 11:00–21:00. Bars: Sun–Thu 11:00–21:00, Fri–Sat to 22:00. Open year-round (reduced line-up in winter).",
        text: "A smaller, central street-food market right across the Inderhavnsbroen bridge from Nyhavn — burgers, tacos, bao, bowls and bars with canal-side seating and a view back over the harbour. From late October an outdoor ice-skating rink opens here for the winter.",
        img: null,
      },
    ],
  },
  {
    kind: "Food halls",
    kindId: "food-halls",
    note: "Roofed, year-round market halls — stalls, delis, bakeries and sit-down bites under one roof, open rain or shine.",
    places: [
      {
        name: "Torvehallerne",
        area: "Nørreport",
        rank: 2,
        coords: [55.6838, 12.5695],
        tags: ["Indoor", "Year-round", "60+ stalls"],
        hours: "Open daily: Mon–Thu 10:00–19:00, Fri 10:00–20:00, Sat 10:00–18:00, Sun 11:00–17:00 (hours vary a little by stall).",
        text: "Copenhagen's glass-roofed market halls by Nørreport — 60-plus stalls of smørrebrød, pastries, coffee, cheese, fresh produce and hot food. A great breakfast or lunch graze, open all year.",
        img: null,
      },
      {
        name: "Tivoli Food Hall",
        area: "Tivoli (Vesterbrogade)",
        coords: [55.6738, 12.566],
        tags: ["Indoor", "Year-round", "No ticket needed"],
        hours: "Bernstorffsgade 3. Open daily 11:00–21:00 (Fri & Sat to 22:00) — no Tivoli ticket needed; the hall has its own street entrance.",
        text: "A modern food hall in the corner of Tivoli with its own entrance, so you don't need a Gardens ticket — stalls for pizza, sushi, Spanish food and more. Packed on summer weekends.",
        img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/1532fb80c94dc928_20261006_193352.webp",
      },
    ],
  },
];

const ALL = GROUPS.flatMap((g) => g.places.map((p) => ({ ...p, kind: g.kind })));

const ITEM_LIST = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Copenhagen's best food market spots",
  description:
    "Copenhagen's best food markets — the open-air street-food yards Reffen and Broens Gadekøkken, and the year-round food halls Torvehallerne and Tivoli Food Hall, with opening hours and map pins.",
  itemListElement: ALL.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `${p.name}, ${p.area}, Copenhagen`,
    url: `${SITE}/copenhagen-food-markets`,
  })),
};

export default function FoodMarketsPage() {
  return (
    <main data-testid="food-markets-page" className="bg-[#F9F8F6] text-black min-h-screen">
      <Seo
        title="Copenhagen's Best Food Market Spots — Street Food & Food Halls"
        description="Where to eat at Copenhagen's food markets: the open-air street-food yards Reffen and Broens Gadekøkken, and the year-round food halls Torvehallerne and Tivoli Food Hall — with opening hours and map pins."
        path="/copenhagen-food-markets"
      />
      <PageSchema
        name="Copenhagen's Best Food Market Spots"
        description="Copenhagen's best food markets — street-food yards and year-round food halls, with opening hours and map pins."
        path="/copenhagen-food-markets"
        crumb="Food markets"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ITEM_LIST)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="A busy food stall at Reffen street-food market in Copenhagen, with a queue in the sun"
            className="w-full h-full object-cover object-[center_40%] opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-24 md:pt-28 pb-12 md:pb-20">
          <div className="flex items-center gap-3 mb-10 md:mb-12">
            <Link
              to="/"
              data-testid="food-markets-back"
              className="inline-flex items-center gap-1.5 font-body font-800 text-sm px-3.5 py-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft size={14} /> Back to start
            </Link>
            <ShareButton
              url="/copenhagen-food-markets"
              title="Copenhagen's Best Food Market Spots"
              data-testid="food-markets-share"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            />
          </div>
          <Reveal>
            <p className="font-body font-800 text-xs md:text-sm uppercase tracking-[0.35em] text-white mb-5">
              WHERE COPENHAGEN GRAZES
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-7xl">
              Copenhagen food
              <br />
              market <span className="font-editorial italic font-600">spots</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-base md:text-lg text-white/75 mt-6 max-w-xl leading-relaxed">
              Two open-air street-food yards and two year-round food halls — the best
              places to graze your way through Copenhagen. Opening hours and a map pin
              for each.
            </p>
            <p data-testid="food-markets-updated" className="font-body text-xs uppercase tracking-[0.2em] text-white/40 mt-4">
              Last updated: 7 October 2026
            </p>
          </Reveal>
        </div>
      </section>

      {/* groups */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20 space-y-14 md:space-y-20">
        {GROUPS.map((group, gi) => (
          <div key={group.kindId} data-testid={`market-group-${group.kindId}`}>
            <Reveal>
              <div className="border-b-2 border-black/10 pb-4 mb-8">
                <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-2">
                  {gi === 0 ? "GO WHEN IT'S SUNNY" : "GOOD RAIN OR SHINE"}
                </p>
                <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-5xl">
                  {group.kind}
                </h2>
              </div>
            </Reveal>
            <Reveal>
              <p className="font-editorial italic text-base md:text-lg text-black/60 -mt-3 mb-8 max-w-2xl">
                {group.note}
              </p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {group.places.map((p, i) => (
                <Reveal key={p.name} y={30} delay={Math.min((i % 2) * 0.06, 0.12)}>
                  <article
                    data-testid={`market-card-${group.kindId}-${i}`}
                    className="h-full bg-white border border-zinc-200 shadow-brut rounded-2xl overflow-hidden flex flex-col"
                  >
                    {p.img ? (
                      <div className="relative h-52 md:h-60 overflow-hidden">
                        <img
                          src={p.img}
                          alt={`${p.name} food market in Copenhagen`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <span className="absolute top-3 left-3 font-body font-800 text-[11px] tracking-wide uppercase text-white bg-black/55 backdrop-blur rounded-full px-3 py-1">
                          {p.area}
                        </span>
                        {p.rank && (
                          <span
                            data-testid={`market-rank-${group.kindId}-${i}`}
                            className="absolute top-3 right-3 font-display font-900 text-[11px] tracking-wide uppercase text-white bg-[#DF3226] rounded-full px-3 py-1 shadow-brut"
                          >
                            Our no. {p.rank}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="relative h-24 bg-[#13141A] flex items-end p-4">
                        <span className="font-body font-800 text-[11px] tracking-wide uppercase text-white/90">
                          {p.area}
                        </span>
                        {p.rank && (
                          <span
                            data-testid={`market-rank-${group.kindId}-${i}`}
                            className="absolute top-3 right-3 font-display font-900 text-[11px] tracking-wide uppercase text-white bg-[#DF3226] rounded-full px-3 py-1 shadow-brut"
                          >
                            Our no. {p.rank}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="p-5 md:p-6 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <a
                          href={mapsUrl(p.name, p.coords)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`market-maps-${group.kindId}-${i}`}
                          className="font-display font-900 tracking-tight text-2xl md:text-3xl hover:text-[#DF3226] hover:underline underline-offset-4 transition-colors"
                        >
                          {p.name}
                        </a>
                        <a
                          href={mapsUrl(p.name, p.coords)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${p.name} in Google Maps`}
                          data-testid={`market-pin-${group.kindId}-${i}`}
                          className="shrink-0 mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F9F8F6] border border-zinc-200 text-[#DF3226] hover:bg-[#DF3226] hover:text-white transition-colors"
                        >
                          <MapPin size={16} />
                        </a>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {p.tags.map((t) => (
                          <span
                            key={t}
                            className="font-body font-700 text-[11px] tracking-wide uppercase text-black/55 bg-[#F9F8F6] border border-zinc-200 rounded-full px-2.5 py-1"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-4 flex-1">
                        {p.text}
                      </p>
                      <div
                        data-testid={`market-hours-${group.kindId}-${i}`}
                        className="mt-4 flex items-start gap-2 text-sm text-black/70 bg-[#F4F2EE] border border-zinc-200 rounded-xl p-3.5"
                      >
                        <Clock size={15} className="shrink-0 mt-0.5 text-[#DF3226]" />
                        <span className="leading-relaxed">{p.hours}</span>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        ))}
        <Reveal>
          <p className="font-body text-xs text-black/40 leading-relaxed max-w-2xl">
            Opening hours change with the season and by stall — always double-check on
            the market's own site before a special trip, especially in winter.
          </p>
        </Reveal>
      </section>

      {/* planner CTA */}
      <section data-testid="food-markets-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Hungry yet?
            <br />
            <span className="font-editorial italic font-600">Drop one into your day</span>
          </h2>
          <Link
            to="/planner"
            data-testid="food-markets-planner-cta"
            className="shrink-0 inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Open the planner
          </Link>
        </div>
      </section>

      <ExploreLinks
        testid="explore-food-markets"
        links={[
          { label: "The best bakeries & a bakery crawl", to: "/bakeries" },
          { label: "Classic smørrebrød spots", to: "/smorrebrod" },
          { label: "The ready-made 3 days — Copenhagen itinerary", to: "/copenhagen-itinerary" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
